import { useState, useMemo } from 'react'
import { cn } from '@/lib/utils'
import { useTranslation } from 'react-i18next'
import { Phone, Mail, Check } from 'lucide-react'
import { contactCTAs } from './config'
import type { ChatMessageProps, ContactCTACopy } from '@brochure/shared'

function parseMarkdownBold(text: string): React.ReactNode[] {
  const parts = text.split(/(\*\*[^*]+\*\*)/g)
  return parts.map((part, index) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return (
        <strong key={index} className="font-medium text-bruno">
          {part.slice(2, -2)}
        </strong>
      )
    }
    return part
  })
}

const ctaIcons: Record<string, React.ReactNode> = {
  phone: <Phone className="w-3 h-3" strokeWidth={1.5} />,
  email: <Mail className="w-3 h-3" strokeWidth={1.5} />,
}

function CopyButton({
  cta,
  label,
  copiedLabel,
}: {
  cta: ContactCTACopy
  label: string
  copiedLabel: string
}) {
  const [copied, setCopied] = useState(false)

  const handleClick = () => {
    navigator.clipboard.writeText(cta.value)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <button
      onClick={handleClick}
      className={cn(
        'inline-flex items-center gap-1.5 px-3 py-1 text-[10px] font-medium tracking-[0.2em] uppercase transition-all duration-300 border',
        copied
          ? 'text-argan-deep border-argan/60 bg-argan/10'
          : 'text-bruno-soft border-argan/30 hover:text-bruno hover:border-argan-deep hover:bg-cream-soft'
      )}
    >
      {copied ? <Check className="w-3 h-3" strokeWidth={1.5} /> : ctaIcons[cta.id]}
      <span>{copied ? copiedLabel : label}</span>
    </button>
  )
}

export function ChatMessage({ message, isTyping = false, isFirst = true }: ChatMessageProps) {
  const { t } = useTranslation('common')
  const isUser = message.role === 'user'

  const formattedContent = useMemo(() => {
    if (!message.content) return null
    return parseMarkdownBold(message.content)
  }, [message.content])

  return (
    <div className={cn('flex flex-col', isUser ? 'items-end' : 'items-start')}>
      {/* Role label — only on first message of a sequence */}
      {!isUser && isFirst && !isTyping && (
        <div className="flex items-center gap-2 mb-1.5 ml-1">
          <span className="block w-3 h-px bg-argan-deep/60" />
          <span className="text-[9px] font-medium tracking-[0.28em] uppercase text-argan-deep">
            {t('chatbot.eyebrow', 'Concierge')}
          </span>
        </div>
      )}

      <div
        className={cn(
          'max-w-[88%] px-4 py-2.5 leading-relaxed',
          isUser
            ? 'bg-bruno text-cream rounded-[2px] rounded-br-none'
            : 'bg-cream text-bruno border border-argan/25 rounded-[2px] rounded-bl-none shadow-[0_2px_10px_-4px_rgba(59,49,46,0.12)]'
        )}
      >
        {isTyping ? (
          <div className="flex items-center gap-1.5 py-1.5 px-1">
            <span
              className="w-1.5 h-1.5 bg-argan-deep/70 rounded-full animate-bounce"
              style={{ animationDelay: '0ms' }}
            />
            <span
              className="w-1.5 h-1.5 bg-argan-deep/70 rounded-full animate-bounce"
              style={{ animationDelay: '150ms' }}
            />
            <span
              className="w-1.5 h-1.5 bg-argan-deep/70 rounded-full animate-bounce"
              style={{ animationDelay: '300ms' }}
            />
          </div>
        ) : (
          <p
            className={cn(
              'text-[13.5px] whitespace-pre-wrap',
              isUser ? 'font-light' : 'font-normal'
            )}
            style={{ lineHeight: 1.6 }}
          >
            {formattedContent}
          </p>
        )}
      </div>

      {/* Contact CTAs — only for assistant messages, not typing */}
      {!isUser && !isTyping && message.content && contactCTAs.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5 mt-2.5 ml-1">
          {contactCTAs.map((cta) =>
            cta.kind === 'copy' ? (
              <CopyButton
                key={cta.id}
                cta={cta}
                label={t(cta.labelKey)}
                copiedLabel={t('chatbot.cta.copied')}
              />
            ) : (
              <a
                key={cta.id}
                href={cta.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 px-3 py-1 text-[10px] font-medium tracking-[0.2em] uppercase text-bruno-soft hover:text-bruno border border-argan/30 hover:border-argan-deep hover:bg-cream-soft transition-all duration-300"
              >
                {cta.icon && <img src={cta.icon} alt="" className="w-3 h-3" />}
                <span>{t(cta.labelKey)}</span>
              </a>
            )
          )}
        </div>
      )}
    </div>
  )
}
